import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAutoExitTimeout } from '@/store/slices/room';
import { useScene } from '@/lib/useCommon';
import logger from '@/utils/logger';

/**
 * 读取 CSS 变量的像素值（例如 '--layout-bottom-controls-offset' -> number）
 * 优先从 documentElement，其次从 body 读取；返回 NaN 表示不可用
 */
function readCssVarPx(varName: string): number {
  if (typeof window === 'undefined') return NaN;
  const docEl = document.documentElement;
  const bodyEl = document.body;
  const rawDoc = getComputedStyle(docEl).getPropertyValue(varName);
  const rawBody = getComputedStyle(bodyEl).getPropertyValue(varName);
  const raw = (rawDoc || rawBody || '').trim();
  if (!raw) return NaN;
  const num = parseInt(raw.replace('px', '').trim(), 10);
  return Number.isFinite(num) ? num : NaN;
}

/**
 * 应用设置项的类型定义
 */
export interface AppSettings {
  pushToTalkMode: boolean;
  recognitionMode: 'local' | 'cloud';
  voiceInterruptAllowed: boolean;
  autoExitTimeout: number;
  bottomControlsOffset: number;
}

/**
 * 设置项配置
 */
export interface SettingItem {
  key: keyof AppSettings;
  label: string;
  tooltip: string;
  type: 'switch' | 'select' | 'slider';
  value: any;
  checked?: boolean;
  options?: Array<{ label: string; value: any }>;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: any) => void;
}

/**
 * 统一的应用设置管理Hook
 * 提供所有设置项的状态管理和sessionStorage同步
 */
export function useAppSettings() {
  const dispatch = useDispatch();
  const room = useSelector((state: any) => state.room);
  const { autoExitTimeout } = room;
  const { isInterruptMode } = useScene();

  // 按住说话模式
  const [pushToTalkMode, setPushToTalkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('pushToTalkMode') === '1';
    }
    return false;
  });

  // 识别模式
  const [recognitionMode, setRecognitionMode] = useState<'local' | 'cloud'>(() => {
    if (typeof window !== 'undefined') {
      return (sessionStorage.getItem('recognitionMode') as 'local' | 'cloud') || 'local';
    }
    return 'local';
  });

  // 语音打断模式状态
  const [voiceInterruptAllowed, setVoiceInterruptAllowed] = useState(() => {
    if (typeof window !== 'undefined') {
      const localSetting = sessionStorage.getItem('voiceInterruptAllowed');
      if (localSetting !== null) {
        return localSetting === '1';
      }
    }
    return isInterruptMode ?? false;
  });

  // BottomControls位置偏移设置（默认24px；若存在布局默认值则以布局值为初始值）
  const [bottomControlsOffset, setBottomControlsOffset] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('bottomControlsOffset');
      if (saved) {
        return parseInt(saved, 10);
      }
      // 无会话覆盖时，读取布局级默认变量
      const layoutDefault = readCssVarPx('--layout-bottom-controls-offset');
      return Number.isFinite(layoutDefault) ? layoutDefault : 24;
    }
    return 24;
  });

  // 当没有会话覆盖时，持续同步显示值为布局默认（适配响应式断点与主题）
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sync = () => {
      const saved = sessionStorage.getItem('bottomControlsOffset');
      if (saved) {
        setBottomControlsOffset(parseInt(saved, 10));
      } else {
        const layoutDefault = readCssVarPx('--layout-bottom-controls-offset');
        setBottomControlsOffset(Number.isFinite(layoutDefault) ? layoutDefault : 24);
      }
    };
    // 立即同步一次
    sync();
    // 响应式/可见性/焦点改变时同步（涵盖媒体查询变化和用户手动清理会话）
    window.addEventListener('resize', sync);
    window.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  // 监听语音打断设置变化
  useEffect(() => {
    const handleSettingChange = () => {
      if (typeof window !== 'undefined') {
        const localSetting = sessionStorage.getItem('voiceInterruptAllowed');
        if (localSetting !== null) {
          setVoiceInterruptAllowed(localSetting === '1');
        }
      }
    };

    window.addEventListener('voice-interrupt-setting-changed', handleSettingChange);
    return () => {
      window.removeEventListener('voice-interrupt-setting-changed', handleSettingChange);
    };
  }, []);

  // 初始化CSS变量（仅在存在用户设置时生效）
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('bottomControlsOffset');
      if (saved) {
        document.documentElement.style.setProperty('--bottom-controls-offset', `${bottomControlsOffset}px`);
      } else {
        // 移除会话级变量，让布局级默认或组件兜底生效
        document.documentElement.style.removeProperty('--bottom-controls-offset');
      }
    }
  }, [bottomControlsOffset]);

  // 计算有效的打断模式
  const effectiveInterruptMode = voiceInterruptAllowed;

  // 处理函数
  const handlePushToTalkModeToggle = useCallback((checked: boolean) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('pushToTalkMode', checked ? '1' : '0');
    }
    setPushToTalkMode(checked);
    logger.info('[useAppSettings] 按住说话模式已更新:', checked);
  }, []);

  const handleRecognitionModeChange = useCallback((mode: 'local' | 'cloud') => {
    setRecognitionMode(mode);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('recognitionMode', mode);
    }
    logger.info('[useAppSettings] 识别模式已更新:', mode);
  }, []);

  const handleInterruptToggle = useCallback((checked: boolean) => {
    // 立即更新本地状态
    setVoiceInterruptAllowed(checked);
    
    // 保存到sessionStorage
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('voiceInterruptAllowed', checked ? '1' : '0');
    }
    
    logger.info('[useAppSettings] 语音打断设置已更新:', checked);
    
    // 触发全局事件通知其他组件
    window.dispatchEvent(new CustomEvent('voice-interrupt-setting-changed', {
      detail: { voiceInterruptAllowed: checked }
    }));
  }, []);

  const handleAutoExitTimeoutChange = useCallback((value: number) => {
    dispatch(setAutoExitTimeout(value));
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('autoExitTimeout', value.toString());
    }
    logger.info('[useAppSettings] 自动退出超时已更新:', value);
  }, [dispatch]);

  const handleBottomControlsOffsetChange = useCallback((value: number) => {
    setBottomControlsOffset(value);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('bottomControlsOffset', value.toString());
      // 更新CSS变量以实现实时预览
      document.documentElement.style.setProperty('--bottom-controls-offset', `${value}px`);
    }
    logger.info('[useAppSettings] BottomControls位置偏移已更新:', value);
  }, []);

  // 设置项配置
  const settingsConfig: SettingItem[] = [
    {
      key: 'recognitionMode',
      label: '识别模式',
      tooltip: '选择语音识别模式：本地识别(离线)或云端识别(在线)',
      type: 'select',
      value: recognitionMode,
      options: [
        { label: '本地识别', value: 'local' },
        { label: '云端识别', value: 'cloud' },
      ],
      onChange: handleRecognitionModeChange,
    },
    {
      key: 'pushToTalkMode',
      label: '启用按住说话',
      tooltip: '启用后，需要按住下方的按钮才能说话',
      type: 'switch',
      value: pushToTalkMode,
      checked: pushToTalkMode,
      onChange: handlePushToTalkModeToggle,
    },
    {
      key: 'voiceInterruptAllowed',
      label: '允许语音打断',
      tooltip: '允许语音打断',
      type: 'switch',
      value: effectiveInterruptMode,
      checked: effectiveInterruptMode,
      onChange: handleInterruptToggle,
    },
    {
      key: 'autoExitTimeout',
      label: '自动退出设置',
      tooltip: '设置无活动时自动退出的时间',
      type: 'select',
      value: autoExitTimeout,
      options: [
        { label: '不超时退出', value: 0 },
        { label: '15秒', value: 15000 },
        { label: '30秒', value: 30000 },
        { label: '1分钟', value: 60000 },
      ],
      onChange: handleAutoExitTimeoutChange,
    },
    {
      key: 'bottomControlsOffset',
      label: '底部控件位置',
      tooltip: '调整底部控件距离屏幕底部的距离（0-500px）',
      type: 'slider',
      value: bottomControlsOffset,
      min: 0,
      max: 500,
      step: 8,
      onChange: handleBottomControlsOffsetChange,
    },
  ];

  // 当前设置值
  const currentSettings: AppSettings = {
    pushToTalkMode,
    recognitionMode,
    voiceInterruptAllowed: effectiveInterruptMode,
    autoExitTimeout,
    bottomControlsOffset,
  };

  return {
    // 当前设置值
    settings: currentSettings,
    // 设置项配置
    settingsConfig,
    // 单独的处理函数（向后兼容）
    handlePushToTalkModeToggle,
    handleRecognitionModeChange,
    handleInterruptToggle,
    handleAutoExitTimeoutChange,
    handleBottomControlsOffsetChange,
    // 单独的状态值（向后兼容）
    pushToTalkMode,
    recognitionMode,
    effectiveInterruptMode,
    autoExitTimeout,
    bottomControlsOffset,
  };
}